"use client";

import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import type { Tournament } from "@/entities/tournament/model/types";
import { useUserProfile } from "@/hooks/useAuth";
import { useTournamentStore } from "@/entities/tournament/model/tournament-store";
import { handleError, extractApiErrorMessage } from "@/shared/api/error-handler";
import { tournamentApi } from "@/shared/api/tournament";
import { filterAccountsForTournamentRegion } from "@/shared/lib/riot-region-compat";
import type {
  TeamRegistrationDTO,
} from "@/shared/api/tournament.schemas";
import { AuthService } from "@/shared/lib/auth-service";
import { userApi, type UserTeam, type RiotAccount } from "@/shared/api/user";
import { AUTO_CLOSE_TIMES } from "@/shared/config";
import { ANY_ROLE, roleFromApi, roleToApi, type RoleSelectValue } from "@/shared/lib/lol-roles";
import { tournamentKeys } from "@/shared/api/query-keys";

/**
 * Контракт соло-регистрации берём у самой API-функции, а не у Zod-схемы
 * SoloPlayerRegistrationDTO: та никем не вызывается и разошлась с бэкендом
 * (роли в нижнем регистре, нет playerNote). Так расхождение поймает компилятор.
 */
type SoloRegistrationPayload = Parameters<typeof tournamentApi.registerSoloPlayer>[0];

export interface TournamentRegistrationState {
  selectedTeam: string;
  selectedRiotAccount: string;
  /** Значение селекта: конкретная роль либо токен «Любая» (см. shared/lib/lol-roles). */
  mainRole: RoleSelectValue;
  secondaryRole: RoleSelectValue;
  desiredRole: "Participant" | "Captain";
  /** Заметка о себе для капитанов, до 200 символов. */
  playerNote: string;
  customFieldValues: Record<string, string>;
  teams: UserTeam[];
  riotAccounts: RiotAccount[];
  isLoading: boolean;
  error: string | null;
  success: boolean;
  dataLoading: boolean;
}

export interface TournamentRegistrationActions {
  setSelectedTeam: (teamId: string) => void;
  setSelectedRiotAccount: (accountId: string) => void;
  setMainRole: (role: RoleSelectValue) => void;
  setSecondaryRole: (role: RoleSelectValue) => void;
  setDesiredRole: (role: "Participant" | "Captain") => void;
  setPlayerNote: (note: string) => void;
  setCustomFieldValues: Dispatch<SetStateAction<Record<string, string>>>;
  register: () => Promise<boolean>;
  reset: () => void;
  loadData: () => Promise<void>;
}

// unused const removed

export function useTournamentRegistration(
  tournament: Tournament,
): TournamentRegistrationState & TournamentRegistrationActions {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const { data: user } = useUserProfile();
  const queryClient = useQueryClient();
  const fetchTournamentById = useTournamentStore(
    (state) => state.fetchTournamentById,
  );
  const fetchTournaments = useTournamentStore((state) => state.fetchTournaments);

  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [selectedRiotAccount, setSelectedRiotAccount] = useState<string>("");
  const [mainRole, setMainRole] = useState<RoleSelectValue>(ANY_ROLE);
  const [secondaryRole, setSecondaryRole] = useState<RoleSelectValue>(ANY_ROLE);
  const [playerNote, setPlayerNote] = useState("");
  const [desiredRole, setDesiredRole] = useState<"Participant" | "Captain">(
    "Participant",
  );
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  // Предзаполнение ролей из профиля (User.PreferredRole/SecondaryRole). Профиль хранит
  // роли в нижнем регистре, бэкенд ждёт с заглавной — приводит roleFromApi, а не
  // рукописный switch с приведением типа (раньше здесь был `as unknown as`, из-за
  // которого тип обещал нижний регистр, а значение приходило с заглавной).
  useEffect(() => {
    if (!user) return;
    // preferredRole/secondaryRole приходят с /me, но их нет в Kubb-схеме профиля
    // (ручка PATCH /me/preferred-role) — читаем через явный тип, не через any.
    const roles = user as {
      preferredRole?: string | null;
      secondaryRole?: string | null;
    };
    // Трогаем только нетронутые поля: перетирать уже выбранное фоновой загрузкой профиля нельзя.
    setMainRole((prev) => (prev === ANY_ROLE ? roleFromApi(roles.preferredRole) : prev));
    setSecondaryRole((prev) => (prev === ANY_ROLE ? roleFromApi(roles.secondaryRole) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [teams, setTeams] = useState<UserTeam[]>([]);
  const [riotAccounts, setRiotAccounts] = useState<RiotAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // removed unused getRankValue

  // Читаем текущий выбранный аккаунт через ref, чтобы НЕ держать его в deps loadData
  // (иначе loadData пересоздаётся при setSelectedRiotAccount внутри себя → эффект
  // перезапускает loadData → двойной фетч teams+accounts при каждом открытии).
  const selectedRiotAccountRef = useRef(selectedRiotAccount);
  selectedRiotAccountRef.current = selectedRiotAccount;

  const loadData = useCallback(async () => {
    if (!user) return;

    setDataLoading(true);
    try {
      const [teamsData, accountsData] = await Promise.all([
        userApi.getUserTeams(String(user.id ?? "")),
        AuthService.getRiotAccounts(),
      ]);

      setTeams(teamsData || []);
      const accounts: RiotAccount[] = (accountsData || []) as RiotAccount[];
      setRiotAccounts(accounts);

      const eligible = filterAccountsForTournamentRegion(accounts, tournament.region);
      const next =
        eligible.find((a) => a.isMain) ??
        eligible.find((a) => (a as { isPrimary?: boolean })?.isPrimary) ??
        eligible[0];

      if (!next) {
        setSelectedRiotAccount("");
        return;
      }

      const current = selectedRiotAccountRef.current;
      if (!current) {
        setSelectedRiotAccount(next.id);
        return;
      }

      const isSelectedEligible = eligible.some((a) => a.id === current);
      if (!isSelectedEligible) {
        setSelectedRiotAccount(next.id);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : tUi("litUseTournamentRegistration.oshibkaZagruzkiDannyh");
      setError(errorMessage);
      handleError(err, {
        component: "useTournamentRegistration",
        action: "loadData",
      });
    } finally {
      setDataLoading(false);
    }
  }, [user, tournament.region, tUi]);

  const register = useCallback(async () => {
    if (!user || !tournament) return false;

    setIsLoading(true);
    setError(null);

    try {
      const isDraftMode = tournament?.format === "Draft";
      // Arena — командный (temp-команды по 3), регистрируется как team (RegisterTeamAsync
      // знает Arena). Без этого Arena проваливалась в solo-fallback → 500.
      const isClassicMode =
        tournament?.format === "Classic" ||
        tournament?.format === "SoloOneVsOne" ||
        tournament?.format === "Arena";

      const customFields =
        Object.keys(customFieldValues).length > 0
          ? Object.fromEntries(
              Object.entries(customFieldValues).filter(([, v]) => (v ?? "").trim() !== ""),
            )
          : undefined;

      if (isDraftMode) {
        const payload: SoloRegistrationPayload = {
          tournamentId: String(tournament.id ?? ""),
          riotAccountId: selectedRiotAccount,
          // Токен «Любая» на бэкенд не уходит: отсутствие роли и означает «любая линия».
          mainRole: roleToApi(mainRole),
          secondaryRole: roleToApi(secondaryRole),
          desiredRole,
          playerNote: playerNote.trim() || undefined,
          customFields,
        };
        const soloResult = await tournamentApi.registerSoloPlayer(payload);
        // Ловушка silent-200: бэкенд может вернуть 200 с success=false
        // (Ok(OperationResultDto)) — без этой проверки UI показывал бы
        // ложный «успех» (см. ai/known-issues.md).
        if (soloResult && soloResult.success === false) {
          throw new Error("Регистрация отклонена сервером");
        }
      } else if (isClassicMode) {
        const payload: TeamRegistrationDTO = {
          tournamentId: String(tournament.id ?? ""),
          teamId: selectedTeam || undefined,
          riotAccountId: selectedRiotAccount,
          customFields,
        };
        const teamResult = await tournamentApi.registerTeam(payload);
        // Ловушка silent-200: бэкенд может вернуть 200 с success=false
        // (Ok(OperationResultDto)) — без этой проверки UI показывал бы
        // ложный «успех» (см. ai/known-issues.md).
        if (teamResult && teamResult.success === false) {
          throw new Error("Регистрация команды отклонена сервером");
        }
      } else {
        // fallback: пробуем solo
        const payload: SoloRegistrationPayload = {
          tournamentId: String(tournament.id ?? ""),
          riotAccountId: selectedRiotAccount,
          // Токен «Любая» на бэкенд не уходит: отсутствие роли и означает «любая линия».
          mainRole: roleToApi(mainRole),
          secondaryRole: roleToApi(secondaryRole),
          desiredRole,
          playerNote: playerNote.trim() || undefined,
          customFields,
        };
        const soloResult = await tournamentApi.registerSoloPlayer(payload);
        // Ловушка silent-200: бэкенд может вернуть 200 с success=false
        // (Ok(OperationResultDto)) — без этой проверки UI показывал бы
        // ложный «успех» (см. ai/known-issues.md).
        if (soloResult && soloResult.success === false) {
          throw new Error("Регистрация отклонена сервером");
        }
      }

      setSuccess(true);
      toast.success(tToast("useTournamentRegistration.registratsiyaNaTurnirProshla"));

      // Инвалидируем кэш турнира + общий список — backend теперь вернёт
      // capabilities.canRegister=false для зарегистрированного юзера, и
      // кнопка «Участвовать» автоматически пропадёт на странице турниров.
      const tournamentId = String(tournament.id ?? "");
      if (tournamentId) {
        await Promise.allSettled([
          fetchTournamentById(tournamentId),
          fetchTournaments(),
          // Ключи из фабрики: и список (кнопка «Участвовать» зависит от
          // capabilities), и сам турнир со всеми его разделами. Раньше тут
          // инвалидировались два написания одного ключа — ["tournaments"] и
          // ["tournament", id], — потому что никто не знал полного списка.
          queryClient.invalidateQueries({ queryKey: tournamentKeys.lists() }),
          queryClient.invalidateQueries({
            queryKey: tournamentKeys.detail(tournamentId),
          }),
        ]);
        // Третий источник состояния: грид участников и панель регистраций слушают
        // window-эвент, а не React Query. Без него после реги грид остаётся stale до
        // релоада («needs page refresh»-класс багов). Паттерн — refreshAfterRegistrationChange
        // в tournament-actions.tsx (см. ai/known-issues.md → «три источника состояния»).
        try {
          window.dispatchEvent(
            new CustomEvent("tournament:participants:refresh", {
              detail: { tournamentId },
            }),
          );
        } catch {
          // ignore (SSR / без window)
        }
      }

      setTimeout(() => {
        setSuccess(false);
      }, AUTO_CLOSE_TIMES.FAST);
      return true;
    } catch (err) {
      const e = err as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      } | null;
      const errorMessage = String(
        extractApiErrorMessage(e, tUi("litUseTournamentRegistration.oshibkaRegistratsiiNaTurnir")),
      );
      setError(errorMessage);
      // Ошибка уже показывается inline через setError → handleError тихий
      // (лог/Sentry), без дублирующего тоста.
      handleError(err, {
        component: "useTournamentRegistration",
        action: "registerForTournament",
        meta: { silent: true },
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [
    user,
    tournament,
    selectedTeam,
    selectedRiotAccount,
    mainRole,
    secondaryRole,
    desiredRole,
    playerNote,
    customFieldValues,
    fetchTournamentById,
    fetchTournaments,
    queryClient, tToast, tUi,
  ]);

  const reset = useCallback(() => {
    setSelectedTeam("");
    setSelectedRiotAccount("");
    setMainRole(ANY_ROLE);
    setSecondaryRole(ANY_ROLE);
    setPlayerNote("");
    setDesiredRole("Participant");
    setCustomFieldValues({});
    setError(null);
    setSuccess(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // removed unused mode flags (handled inside register)

  return {
    selectedTeam,
    selectedRiotAccount,
    mainRole,
    secondaryRole,
    desiredRole,
    playerNote,
    customFieldValues,
    teams,
    riotAccounts,
    isLoading,
    error,
    success,
    dataLoading,
    setSelectedTeam,
    setSelectedRiotAccount,
    setMainRole,
    setSecondaryRole,
    setPlayerNote,
    setDesiredRole,
    setCustomFieldValues,
    register,
    reset,
    loadData,
  };
}
